/**
 * Admin panel — English translations.
 *
 * Shape must match `adminMessagesPl` (TypeScript enforces this via the
 * `AdminMessagesShape` type guard on `getAdminMessages()`).
 */

import type { AdminMessagesShape } from "./pl";

const enPlural = new Intl.PluralRules("en");

export const adminMessagesEn: AdminMessagesShape = {
  topBar: {
    adminPanelLink: {
      title: "Open Imperial admin panel in new tab",
      label: "Imperial admin panel",
    },
    devBannerToggle: {
      titleShown: "Hide developer banner (AlignmentChecker)",
      titleHidden: "Show developer banner (AlignmentChecker)",
      ariaShown: "Hide developer banner",
      ariaHidden: "Show developer banner",
    },
    devMockDropDownTreeToggle: {
      titleOn: "Disable mock 5-level tree in WDropDownMenu",
      titleOff: "Enable mock 5-level tree in WDropDownMenu",
      ariaOn: "Disable mock 5-level tree",
      ariaOff: "Enable mock 5-level tree",
    },
    editModeToggle: {
      titleOpen: "Layout management open — click to close",
      titleClosed: "Open page layout & windows management panel",
      label: "Manage page layout and windows",
    },
    notificationsBell: {
      ariaLabel: (count: number) => `Notifications (${count} unread)`,
      emptyState: "No notifications",
    },
    tabCatalog: {
      titleOpen: "Catalog management open — click to close",
      titleClosed: "Open catalog management panel",
      label: "Manage catalogs",
    },
    themeToggle: {
      title: (label: string) => `Theme: ${label} (click to change)`,
      ariaLabel: (label: string) => `Change theme, current: ${label}`,
      modes: {
        light: "light",
        dark: "dark",
        funeral: "funeral",
        hc: "high contrast",
      },
    },
    userMenu: {
      yourAccount: "Your account",
      logout: "Log out",
      loggingOut: "Logging out...",
      toastLoggedOut: "Logged out from Imperial.",
      toastError: (reason: string) => `Logout error: ${reason}`,
      toastErrorUnknown: "unknown",
    },
    adminLocaleSwitch: {
      triggerTitle: (label: string) =>
        `Managing version: ${label}. Click to switch.`,
      menuAriaLabel: "Managed languages",
      menuHeader: "Manage language version",
      menuFooter: "Each language version has its own layout + windows.",
    },
  },

  backendFailures: {
    heading: (count: number) =>
      `The backend failed to answer ${count} ${enPlural.select(count) === "one" ? "request" : "requests"}`,
    lead:
      "This page is rendered incomplete. Only a signed-in administrator sees this message - visitors see the page without the missing parts.",
    impactCmsConfig:
      "No domain configuration (logo, theme, contact data) - page chrome renders with empty values.",
    impactCmsLayouts:
      "Empty layout list in the panel - the layout of this page cannot be changed.",
    impactUnpublishedWindows:
      "Empty window library - no window can be added to this page.",
    impactDomainLanguages:
      "No language version list - the language switch in the admin bar is unavailable.",
    impactTemplatePreview:
      "Default layout preview unavailable.",
  },

  pendingBar: {
    ariaLabel: (count: number) =>
      `Working — ${count} ${pluralRequestsEn(count)} pending`,
    statusLabel: (count: number) => `Working… (${count})`,
  },

  catalogToolbar: {
    ariaLabel: "Catalog management",
    closeAria: "Close",
    closeTitle: "Close",
    addRootTitle:
      "Open Imperial Administrator > Catalogs > add form (new tab)",
    addRootLabel: "Add new catalog",
    addRootHint: "Opens Imperial Administrator in a new tab",
    manageExistingHeading: "Want to manage an existing catalog?",
    manageExistingBody:
      'From the homepage you can only add a new catalog. To configure, edit content, or delete an existing catalog, navigate to it (e.g. click a menu link, or type the URL manually). The "Manage catalogs" tab opened on a catalog subpage will show actions for that catalog.',
    addChildTitle:
      "Open Imperial Administrator > Catalogs > Add — with id_parent preset (new tab)",
    addChildLabel: "Add subpage",
    addChildHint: "As a child of this catalog",
    configureTitle:
      "Open Imperial Administrator > Catalogs > Edit — name, symbol, layout, metatags (new tab)",
    configureLabel: "Configure",
    configureHint: "Name, symbol, layout, metatags",
    manageContentTitle:
      "Open Imperial Administrator > Edit catalog content (new tab)",
    manageContentLabel: "Manage content",
    manageContentHint: "Edit articles / products / posts",
    deleteConfirmTitle: "Delete catalog?",
    deleteConfirmMessage: (catalogSymbol: string, idCatalog: number) =>
      `Are you sure you want to delete catalog "${catalogSymbol}" (ID ${idCatalog})? This is irreversible — the catalog will be removed together with all its content (articles, products etc.) and window/layout assignments.`,
    deleteConfirmLabel: "Delete catalog",
    cancelLabel: "Cancel",
    currentCatalogLabel: "Current catalog:",
    currentCatalogMeta: (id: number | null, slug: string) =>
      `ID ${id ?? "?"} · path ${slug}`,
    deleteCardLabel: "Delete",
    deleteCardHint: "Requires confirmation",
    deleteCardTitle:
      "Delete catalog — requires confirmation, operation is irreversible",
    missingIdCatalogNote:
      "No id_catalog in context — the page wasn't recognized as a catalog. Check backend [...slug] resolution.",
    /* Disabled-state messages — when admin lacks per-catalog permissions
       from `imp_user_group_privilege`. Backend `IUSER_PRIVILEGES::hasCatalogPrivilege`
       is SoT — frontend gates buttons symmetrically (modify / delete). */
    noPermissionAddChildTitle:
      "Permission denied: you cannot add subcatalogs under this catalog. Contact an administrator to get \"modify\" access for this catalog.",
    noPermissionConfigureTitle:
      "Permission denied: you cannot edit this catalog's properties (name, symbol, layout, metatags). Required: \"modify\" for this catalog in your user group.",
    noPermissionManageContentTitle:
      "Permission denied: you cannot edit this catalog's content. Required: \"modify\" for this catalog in your user group.",
    noPermissionDeleteTitle:
      "Permission denied: you cannot delete this catalog. Required: \"delete\" for this catalog plus full catalog privileges or owner status (editors are blocked from deletion).",
    permissionBadgeLabel: "Your permissions:",
    permissionBadgeFull: "Full access",
    permissionBadgeOwner: "Catalog owner",
    permissionBadgeRedactor: "Editor (limited)",
    permissionBadgeRestricted: "Restricted permissions",
    /* Print to PDF (replaces legacy IPRINT) - shown only when the PDF generator
       extension + Chromium engine are available (`pdf_export_available`). */
    printPdfLabel: "Print to PDF",
    printPdfLabelBusy: "Generating...",
    printPdfHint: "Download the whole page",
    printPdfTitle: "Download the entire current page as a PDF file",
    noPermissionPrintPdfTitle:
      "Permission denied: you cannot generate a printout of this page. Required: content management (\"manage_content\").",
    printPdfSuccess: "PDF generated - download started.",
    printPdfError: "Failed to generate the PDF file. Please try again.",
  },

  layoutPanel: {
    columnHeading: "Page layout management",
    noLayoutsForTemplatePlaceholder: (template: string) =>
      `Template "${template}" — no layouts assigned`,
    layoutChangeReloadHint:
      "After the change, the page will reload with the new area structure.",
    inheritsDefaultBannerPrefix: "This subpage inherits the",
    inheritsDefaultBannerBold: "default layout",
    inheritsDefaultBannerSuffix:
      ". Any change you commit will create its own configuration for this subpage (the default layout stays untouched — to update it, use the button below).",
    modifiedFromDefaultBannerPrefix: "This subpage has",
    modifiedFromDefaultBannerBold: "its own configuration different from the default layout",
    modifiedFromDefaultBannerSuffix:
      ". To revert to the default layout, use the \"Restore default settings\" button below.",
    noDefaultBannerBold: "No default layout saved",
    noDefaultBannerSuffix:
      " for subpages. Configure layout + windows as you wish, then click the button below to make new subpages inherit this configuration.",
    saveDefaultDisabledNoLayouts:
      "First assign layouts to the active template in Imperial Administrator",
    saveDefaultTooltipOverwrite:
      "Overwrite the existing default layout with the current configuration",
    saveDefaultTooltipFresh:
      "Save the current layout + windows as default for subpages",
    overwriteDialogTitle: "Overwrite existing default layout?",
    saveAsDefaultDialogTitle: "Save as default layout?",
    overwriteDialogMessageFull:
      "The existing default layout WILL BE OVERWRITTEN with the current configuration. All subpages using the default will immediately show the new layout. This operation cannot be undone — continue?",
    saveDefaultDialogMessageFull:
      "The current layout + windows will be saved as default for all catalogs that do not have their own configuration.",
    changeLayoutDialogTitle: "Change page layout",
    changeLayoutDialogMessage: (
      totalWindows: number,
      affectedCount: number,
      areaNames: string,
    ) => {
      const windowsWord = totalWindows === 1 ? "window" : "windows";
      const areasWord = affectedCount === 1 ? "area" : "areas";
      return `This change will remove ${totalWindows} ${windowsWord} from ${areasWord}: ${areaNames}.\n\nWindows will return to the "Windows library" panel on the right — you'll be able to drag them to another area.`;
    },
    changeLayoutConfirmLabel: "Change layout",
    cancelLabel: "Cancel",
    layoutChangedReturnedToast: (totalWindows: number) => {
      const phrase =
        totalWindows === 1 ? "window returned" : "windows returned";
      return `Layout changed — ${totalWindows} ${phrase} to the unpublished list`;
    },
    layoutChangedToast: (layoutName: string) =>
      `Layout changed to "${layoutName}"`,
    defaultLayoutOnlySubpageError:
      "Default layout can only be saved from a catalog subpage.",
    pickLayoutFirstError: "Please pick a layout in the field above first.",
    overwriteDefaultMessageSubpage:
      "The existing default layout WILL BE OVERWRITTEN with the current configuration. All subpages using the default will immediately switch to the new layout.",
    overwriteDefaultMessageHomepage:
      "The existing default layout WILL BE OVERWRITTEN with the current configuration.",
    overwriteConfirmLabel: "Yes, overwrite",
    saveAsDefaultConfirmLabel: "Save as default",
    saveDefaultErrorToast: (reason: string) =>
      `Failed to save default layout: ${reason}.`,
    overwriteDefaultSuccessToast: (windowsInserted: number) =>
      `Default layout overwritten (${windowsInserted} windows).`,
    saveDefaultSuccessToast: (windowsInserted: number) =>
      `Default layout saved (${windowsInserted} windows).`,
    panelAriaLabel: "Windows management",
    closeAria: "Close",
    closePanelTitle: "Close panel",
    subpageLayoutHeading: "Subpage layout",
    homepageLayoutHeading: "Homepage layout",
    noTemplateSetPlaceholder: "Domain template not set",
    mismatchOptionLabel: (symbol: string) =>
      `Saved: ${symbol} (invalid for this page)`,
    mismatchBanner: (saved: string, previewed: string) =>
      `This page has layout "${saved}" saved, which is not on the list of available layouts. The area preview below comes from layout "${previewed}" and does NOT reflect what is stored - windows assigned to areas outside that layout will not render on the page. Pick the correct layout from the list to fix it.`,
    helpTextSubpage:
      "Layout for this subpage. The change only affects this URL.",
    helpTextHomepage:
      "Homepage layout. Each subpage has its own independent layout.",
    helpTextSuffix: "After the change the page",
    overrideHint:
      'This change will create a separate configuration for this subpage (instead of inheriting from the template — use the "Save as default" button below).',
    noDefaultWarning:
      'No default — new subpages will need a layout picked manually. Click "Save as default" to make new subpages inherit the current configuration.',
    overrideDefaultLabel: "Overwrite default layout",
    saveAsDefaultLabel: "Save as default",
    addWindowTitle: "Add new window in Imperial Administrator (opens new tab)",
    allTypesOption: "All types",
    deleteWindowConfirmLabel: "Delete window",
    deleteWindowSuccessToast: (name: string) =>
      `Window "${name}" has been deleted.`,
    deleteWindowErrorToast: (name: string, reason: string) =>
      `Failed to delete window "${name}": ${reason}`,
    notesAriaLabel: "Administrator notes for this window (hover to view)",
    noNotesAriaLabel: "No administrator notes",
    noNotesTitle:
      "No administrator notes for this window. Fill in the description field in Imperial Administrator > Windows Manager > Edit window.",
    openConfigTitle:
      "Open window configuration page in Imperial Administrator (new tab)",
    openContentEditorTitle:
      "Open window content editor in Imperial Administrator (new tab)",
    contentEditorBlockedDefault:
      "This component does not provide content management.",
    deleteWindowTitle: "Delete this window completely from the database",
    emptyLibraryMessage:
      'All available windows are already on this page. To create a new one, click "Add".',
    miniMapAriaLabel: "Page layout mini-map",
    discardChangesTitle: "Discard all unsaved changes",
    commitChangesTitle: "Save changes and show on the page",
    areaUnavailableAria: (label: string) =>
      `${label} — area unavailable for windows`,
    detachFromAreaTitle:
      "Detach from area — window will return to the unpublished list",
    detachFromAreaShort:
      "Detach from area (returns to unpublished list)",
    addButtonLabel: "Add",
    sectionCollapseTitle: "Collapse section",
    sectionExpandTitle: "Expand section",
    libraryDropHint:
      "Drag a window onto the layout below to add it to this page. A window can be used on multiple pages at once.",
    componentFilterTitle: "Filter by component type",
    componentFilterAriaLabel: "Component type",
    searchPlaceholder: "Search window…",
    deleteWindowDialogTitle: "Delete window?",
    deleteWindowDialogMessage: (name: string) =>
      `Are you sure you want to delete window "${name}" COMPLETELY? This is irreversible. The window will be removed from the database together with its configuration and any content.`,
    tileExpandedTooltip: "Click again to collapse the details",
    tileCompatibleTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — click for details, drag to publish`,
    tileIncompatibleTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — click for details (not available in the current layout)`,
    tileCollapseHint: "click to collapse",
    actionWindowConfig: "Window configuration",
    actionManageContent: "Manage window content",
    actionDeleteWindow: "Delete window",
    noWindowsMatchingFilters:
      "No windows match the filters. Clear the type filter or search phrase.",
    pageLayoutHeading: "Page layout",
    dragWindowFromListHint:
      "Drag a window from the list above to the chosen area. Changes will appear on the page after clicking",
    commitChangesEmphasis: "Commit changes",
    saveScopeHeading: "Save scope",
    saveScopeThisUrl: "Only this URL",
    saveScopeThisUrlHint: "Highest priority. Changes apply to this URL only — the catalog page and other subpages stay unchanged.",
    saveScopeAllArticles: "All articles in catalog",
    saveScopeAllArticlesHint: "Medium priority. Changes affect all subpages of the catalog — the catalog page itself stays unchanged. Individual per-URL overrides will be cleared.",
    saveScopeCatalog: "Whole catalog",
    saveScopeCatalogHint: "Lowest priority. Changes affect the catalog page AND all of its subpages. Individual overrides (per-URL and per-article) will be cleared.",
    saveScopeNarrowerDisabledOnCatalogIndex:
      "This option is only available on a subpage of the catalog (URL different from the catalog main page). On the catalog page you can only save the \"Whole catalog\" scope.",
    mapEmptyPickLayout: "Pick a layout to see the areas.",
    mapNoBodyAreas: "The selected layout has no body areas.",
    mapSectionHeader: "Header",
    mapSectionHero: "Hero (above content)",
    mapSectionBody: (layoutLabel: string) => `Content (layout: ${layoutLabel})`,
    mapSectionBelowCatalog: "Below catalog content",
    mapSectionFooter: "Footer",
    mapAreaHeaderCol1: "Header 1",
    mapAreaHeaderCol2: "Header 2",
    mapAreaHeaderCol3: "Header 3",
    mapAreaHeaderCol4: "Header 4",
    mapAreaHeaderBottom: "Header — bottom row",
    mapAreaFooterLeft: "Footer left",
    mapAreaFooterMiddle: "Footer middle",
    mapAreaFooterRight: "Footer right",
    mapCommitMessage:
      "You have unsaved changes. The page will not be updated until you click",
    mapCancelButton: "Undo",
    mapCommitButton: "Commit changes",
    mapAreaBlockedHint: "Content rendered from catalog",
    mapAreaEmptyIncompatible: "Incompatible for this window",
    mapAreaEmptyDropHere: "Drop window here",
    resetToDefaultsLabel: "Restore default settings",
    resetToDefaultsTooltip:
      "Removes all layout, windows and areas settings for the ENTIRE catalog (the catalog page and all of its articles) — the catalog will fall back to the saved template defaults.",
    resetToDefaultsDisabledNoCustom:
      "This catalog already uses the template defaults — nothing to restore.",
    saveDefaultDisabledNoChanges:
      "This page already uses the template defaults — change something in the layout, windows or areas to enable save.",
    editLockedInPreviewTooltip:
      "Editing is locked while previewing the default layout. Turn off preview to continue.",
    editLockedNoPermissionTooltip:
      "You don't have permission to edit the page layout. Contact your administrator to request access.",
    deleteWindowNoPermissionTooltip:
      "You don't have permission to delete windows. Contact your administrator to request access.",
    previewModeBannerTitle: "Default layout preview mode",
    previewModeBannerHint:
      "you are viewing the page with the template default configuration, editing is locked.",
    previewModeShowLabel: "Show default layout",
    previewModeShowTooltip:
      "Toggles the page view to the default layout (read-only). Editing will be locked until you disable preview.",
    previewModeExitLabel: "Exit preview",
    previewModeExitTooltip: "Returns to the page's actual configuration.",
    resetToDefaultsDialogTitle: "Restore default settings?",
    resetToDefaultsDialogMessage:
      "This will affect the ENTIRE catalog — the catalog page and ALL of its subpages / articles. All custom layout, windows and areas settings (including per-article overrides) will be removed. The entire catalog will use the saved template defaults. This action cannot be undone. The page will reload once finished.",
    resetToDefaultsConfirmLabel: "Restore defaults for the entire catalog",
    resetToDefaultsSuccessToast: (totalDeleted: number) =>
      `Settings for the entire catalog restored to defaults (${totalDeleted} ${totalDeleted === 1 ? "entry" : "entries"} removed). Reloading the page…`,
    resetToDefaultsErrorToast: (reason: string) =>
      `Failed to restore defaults: ${reason}. Refresh the page to see the actual state.`,
    mapAreaDisabledHint: "Area disabled — not rendered on the page",
    mapAreaEnableAria: (label: string) => `Enable area "${label}"`,
    mapAreaDisableAria: (label: string) => `Disable area "${label}"`,
    detachFromAreaLabel: "Detach from area",
    detachWindowAria: (name: string) => `Detach "${name}" from area`,
    mapWindowMoveTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — click for details, drag to move`,
  },

  alignmentChecker: {
    issue: {
      windowNoAllowedAreas: (name: string) =>
        `Window "${name}" has no allowed areas configured — it cannot be published on any page.`,
      windowNoMatchInLayout: (name: string, layoutLabel: string) =>
        `Window "${name}" has allowed areas configured, but none of them exist in the current layout "${layoutLabel}".`,
      containerNotRegistered: (sym: string) =>
        `Container "${sym}" is assigned to windows in the database, but Imperial CMS has no implementation for it in the registry.`,
      noRendererInReactCms: (sym: string) =>
        `Component "${sym}" has a working getApiContent method in the backend, but Imperial CMS has no renderer.`,
      noLayoutsForTemplate: (activeTemplate: string, pageTypeLabel: string) =>
        `Active template "${activeTemplate}" has no layouts assigned for ${pageTypeLabel}. The page cannot be rendered — the system does not use layouts from other templates (they would clash with the active template's styling).`,
      windowsWithoutContainerMapping: (count: number, templateLabel: string) => {
        const windowsWord = count === 1 ? "window" : "windows";
        const hasWord = count === 1 ? "is missing" : "are missing";
        const configWord = count === 1 ? "a configured container" : "configured containers";
        return `${count} ${windowsWord} on this subpage ${hasWord} ${configWord}${templateLabel}. Visitors see them with a fallback (container "clear" — no wrapper), but the admin should add the mapping in the Imperial panel.`;
      },
      catalogWithoutContainerMapping: (
        catalogName: string,
        componentSymbol: string,
        templateLabel: string,
      ) =>
        `Catalog "${catalogName}" (${componentSymbol}) has no configured container${templateLabel}. Visitors see catalog content with a fallback (container "catalog_empty" — no wrapper), but the admin should add the mapping in the Imperial panel.`,
    },
    pageType: {
      homepage: "the homepage",
      subpage: "subpages",
    },
    issueLabels: {
      window_no_allowed_areas: "WINDOW WITHOUT ALLOWED_AREAS",
      window_no_match_in_layout: "WINDOW NO MATCH IN LAYOUT",
      no_layouts_for_domain: "NO LAYOUTS FOR DOMAIN",
      no_layouts_for_template: "NO LAYOUTS FOR TEMPLATE",
      container_not_registered: "CONTAINER NOT REGISTERED",
      no_renderer_in_react_cms: "NO RENDERER IN IMPERIAL CMS",
      cms_frontend_url_not_set: "CMS FRONTEND URL NOT SET",
      windows_without_container_mapping: "CONTAINER MAPPING MISSING",
      catalog_without_container_mapping: "CATALOG CONTAINER MAPPING MISSING",
    },
    bannerAriaLabel: "Developer banner — Alignment Checker",
    closeButton: {
      title: "Hide banner (restore via the gear icon in the admin bar)",
      ariaLabel: "Hide developer banner",
    },
    devBadge:
      "DEVELOPER BANNER · visible only in development mode (NODE_ENV=development)",
    fullDocs: "Full documentation",
    openInImperialAdmin: "Open in Imperial Administrator",
    sectionDomain: (domain: string) => `Current domain (${domain})`,
    linkDomainComponents: "Components in the domain",
    linkComponentContainers: "Component containers",
    linkDomainTemplates: "Templates in the domain",
    linkTemplateLayouts: (template: string | null) =>
      template ? `Layouts for template ${template}` : "Layouts of the active template",
    linkDomainLanguages: "Languages in the domain",
    sectionCmsRegistry: "CMS — global registries",
    linkTemplates: "Templates",
    linkLayouts: "Layouts",
    linkComponents: "Components",
    linkContainers: "Containers",
    sectionRestApi: "REST API",
    linkApiKeys: "API keys + Swagger",
    sectionDocs: "Documentation",
    linkReactCmsDocs: "Imperial CMS docs",
    actionOpenAddContainerMapping:
      "Open in Imperial Administrator — add container mapping",
    readMoreLinkPrefix: "Read more",
    contextNone: "(none)",
    contextLabel: {
      domain: "Domain:",
      template: "Template:",
      pageType: "Page type:",
      layout: "Layout:",
      locale: "Locale:",
      layoutsForTemplate: "Template layouts:",
    },
    headerOk: "System check — OK",
    headerIssues: (count: number) =>
      `System check — ${count} ${count === 1 ? "issue" : "issues"}`,
    subtitleEnv: (nodeEnv: string) =>
      `Layouts, areas, SSO, Imperial ↔ Imperial CMS configuration · NODE_ENV=${nodeEnv}`,
    okMessage:
      "The system is correctly configured — layouts, areas, SSO, and Imperial ↔ Imperial CMS connections all match.",
    docsAnchor: {
      configCors: "Configuration (CORS, API keys)",
      issueTypes: "AlignmentChecker — issue types reference",
      addLayoutArea: "How to add a layout / area",
      layoutsDbBinding: "Layouts — DB binding",
      areasDocs: "Areas (body + chrome + catalog content)",
      restApiIntegration: "REST API integration",
      templatesDbBinding: "Templates — DB binding",
    },
    generatePackage: {
      buttonLabel: "Create package",
      buttonTitle:
        "Generate ZIP with Imperial CMS source code for deployment to a target domain (target user: unzip + npm install + npm run build + npm start)",
      inProgress: "Generating installation package…",
      success: (filename: string) => `Package ready: ${filename}`,
      errorBuildMissing:
        "Missing required project directory. Verify the react-cms structure.",
      errorForbidden:
        "Endpoint only available in development mode (NODE_ENV=development).",
      errorGeneric: (reason: string) => `Failed to create package: ${reason}`,
    },
  },

  sessionWatcher: {
    toastExpired:
      "Imperial session expired — you have been logged out. Refresh or log in again in Imperial admin.",
    toastLoggedIn: (userId: number | null) =>
      userId ? `Logged in to Imperial (user #${userId}).` : "Logged in to Imperial.",
    toastUserChanged: (userId: number | null) =>
      userId ? `Imperial user changed to #${userId}.` : "Imperial user changed.",
  },

  windowAdminControls: {
    toolbarAriaLabel: (name: string) =>
      `Manage window "${name}" on this page`,
    lockHint: "Click to pin the window toolbar",
    lockedHint: "Toolbar pinned — click again to unpin",
    configAction: "Window configuration",
    manageContentAction: "Manage window content",
    manageContentBlockedTitle: (reason: string) =>
      `Content management unavailable: ${reason}`,
  },

  contextToasts: {
    layoutSavedSuccess: "Page layout saved",
    layoutSaveError: (reason: string) =>
      `Failed to save page layout: ${reason}. Refresh the page to see the actual state.`,
    areasSaveError: (failures: string) =>
      `Failed to save changes in areas: ${failures}. Refresh the page to see the actual backend state.`,
    areaStateSaveError: (reason: string) =>
      `Failed to toggle area visibility: ${reason}. Change reverted — refresh the page to see the actual state.`,
    areaEnabledSuccess: (areaName: string) => `Area "${areaName}" enabled`,
    areaDisabledSuccess: (areaName: string) => `Area "${areaName}" disabled`,
    layoutSaveErrorBeforeWindows: (reason: string) =>
      `Failed to save page layout: ${reason}. Window changes will not be visible until the layout is saved.`,
    areaSavedSingle: (areaName: string) => `Saved area "${areaName}"`,
    areasSavedPlural: (count: number) =>
      `Saved ${count} ${count === 1 ? "area" : "areas"}`,
    unknownError: "unknown error",
  },
};

function pluralRequestsEn(n: number): string {
  return enPlural.select(n) === "one" ? "request" : "requests";
}
